/**
 * @lines98/client/components/meta-icons — глифы для меты «Ювелир». Финальные PNG:
 * монета (ICON_GOLD), осколок (ICON_SHARD), 7 самоцветов (GEM_IMG / gemGlyph +
 * gemEmptyGlyph-силуэт), пять изделий-«артефактов» = ЗВЕРИ (JEWELRY_IMG) — owner-
 * directive 2026-06-10. Пока PLACEHOLDER inline-SVG: искра / карат. Арт — docs/art/*.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: арт-стиль Lines 98 ещё НЕ зафиксирован (CLAUDE.md;
 * meta-jeweler-art-assets.md §0/§12 Q-1). До утверждения арта реворд-блок (Срез
 * 1b) и HUD должны иметь визуалы — это простые, читаемые в HUD-floor (§1.4) SVG-
 * плейсхолдеры, ЛОГИЧЕСКИ соответствующие art-assets.md (монета = круг + линия
 * шаров §3; осколок = угловатый самородок §4; самоцвет = гранёный цветной §2;
 * лоток = 7-ячеистая кассета §9; искра артефакта = вспышка §5.2; карат = бриллиант-
 * в-лучах §8.1). **Свопабельны без переписывания экранов:** один источник глифа +
 * единый класс `meta-glyph` — финальный SVG заменит константу здесь, верстка
 * (reward-block / currency-hud) не меняется. НЕ блокируемся на финальном арте.
 *
 * SCOPE: статичные SVG-строки + цветовая привязка самоцвета к `colorIndex` (тот
 * же `--ball-*` канон, что движок — meta-jeweler-art-assets.md §2.2: «самоцвет
 * цвета X читается как тот же цвет X, что и шар colorIndex=X»). NOT IN SCOPE:
 * финальный арт (art при фиксации стиля), анимация (поверх — reward-block).
 *
 * Размеры — НЕ заданы в SVG (viewBox-only); потребитель масштабирует через CSS
 * (`.meta-glyph { width/height }`), как icon-button. Light/dark-нейтральность
 * (§1.3) — собственный контур-оправа (тёмная врезка + светлый блик через stroke).
 *
 * Cross-references:
 *   - meta-jeweler-art-assets.md §2/§3/§4/§8/§9 (словесное ТЗ под каждый ассет).
 *   - components/icon-button.ts (ICON_* SVG-константы — образец стиля).
 *   - docs/prototype/lines98-concept.html (--ball-1..7 канон-цвета colorIndex).
 */

import type { ColorIndex } from '@lines98/shared/types/game';

// «Животные» (5 изделий-зверей: улитка/ёжик/лягушка/хомяк/пчела) УДАЛЕНЫ вместе с
// мастерской (workshop-animals-removal.md, owner-directive п.14 2026-06-21): импорты
// спрайтов + JEWELRY_IMG + jewelryGlyph сняты. Камни/гномы/валюты-глифы — целы.

// Валюты «золото»/«осколки» — финальные PNG (owner-directive 2026-06-10, итерация 2
// арт-интеграции). Прозрачный фон, 256×256 (как звери) — см. docs/art/coin-export,
// docs/art/shard-export. Тот же swap-path: константа-источник глифа → потребители
// (currency-hud / reward-block / jewelry-card / badge-grid) не меняются.
import coinUrl from '../assets/currency/coin.png';
import shardUrl from '../assets/currency/shard.png';

// Самоцветы — финальные PNG (owner-directive 2026-06-10, итерация 3). У КАЖДОГО цвета
// СВОЯ форма (капля/изумруд/round — gem_size в docs/art/export/gems.json), прозрачный
// фон, equal-area 256×256. Индекс файла = colorIndex (ball-1=red … ball-7=orange,
// сверено с --ball-N в global.css). Пустое гнездо берёт СИЛУЭТ того же спрайта через
// CSS-маску (см. gemEmptyGlyph) → форма гнезда = форма камня, тот же размер/позиция.
import gemBlueUrl from '../assets/gems/blue.png';
import gemCyanUrl from '../assets/gems/cyan.png';
import gemGreenUrl from '../assets/gems/green.png';
import gemOrangeUrl from '../assets/gems/orange.png';
import gemPinkUrl from '../assets/gems/pink.png';
import gemRedUrl from '../assets/gems/red.png';
import gemYellowUrl from '../assets/gems/yellow.png';

// Гномы — 7 спрайтов-карточек (мета «Гномы», `meta-gnomes.md` §4.2, owner-directive
// 2026-06-13). PNG на прозрачном фоне, нормализованы по площади (equal-area, см.
// docs/art/gnomes/gnomes.json) → одинаково «весомы» в карточке. Индекс файла =
// colorIndex (gnome_1_red … gnome_7_orange, сверено с --ball-N). Тот же swap-path, что
// у зверей/камней: один источник глифа на цвет → потребитель (`components/gnome-card`)
// масштабирует через CSS `.meta-glyph`, экран НЕ меняется.
import gnomeBlueUrl from '../assets/gnomes/sprites/blue.png';
import gnomeCyanUrl from '../assets/gnomes/sprites/cyan.png';
import gnomeGreenUrl from '../assets/gnomes/sprites/green.png';
import gnomeOrangeUrl from '../assets/gnomes/sprites/orange.png';
import gnomePinkUrl from '../assets/gnomes/sprites/pink.png';
import gnomeRedUrl from '../assets/gnomes/sprites/red.png';
import gnomeYellowUrl from '../assets/gnomes/sprites/yellow.png';

// Full-арт гномов (в полный рост) — экран «провал» (`meta-gnomes.md` §5.3 Surface C, GN-6).
import gnomeFullBlueUrl from '../assets/gnomes/full/blue.png';
import gnomeFullCyanUrl from '../assets/gnomes/full/cyan.png';
import gnomeFullGreenUrl from '../assets/gnomes/full/green.png';
import gnomeFullOrangeUrl from '../assets/gnomes/full/orange.png';
import gnomeFullPinkUrl from '../assets/gnomes/full/pink.png';
import gnomeFullRedUrl from '../assets/gnomes/full/red.png';
import gnomeFullYellowUrl from '../assets/gnomes/full/yellow.png';

/** colorIndex 1..7 → URL спрайта самоцвета (своя форма на цвет). Канон-привязка к --ball-N. */
const GEM_IMG: Record<ColorIndex, string> = {
  1: gemRedUrl,
  2: gemGreenUrl,
  3: gemBlueUrl,
  4: gemYellowUrl,
  5: gemPinkUrl,
  6: gemCyanUrl,
  7: gemOrangeUrl,
};

/**
 * colorIndex 1..7 → URL спрайта-карточки гнома (`meta-gnomes.md` §4.2). Стабильная
 * привязка к индексам движка (= --ball-N), НЕ к палитре скина: гном цвета X = гном,
 * который добывает камень X. Канон-порядок арт-ассетов gnomes.json.
 */
const GNOME_IMG: Record<ColorIndex, string> = {
  1: gnomeRedUrl,
  2: gnomeGreenUrl,
  3: gnomeBlueUrl,
  4: gnomeYellowUrl,
  5: gnomePinkUrl,
  6: gnomeCyanUrl,
  7: gnomeOrangeUrl,
};

/** colorIndex 1..7 → URL full-арта гнома (в полный рост) — экран «провал» (GN-6, §5.3). */
const GNOME_FULL_IMG: Record<ColorIndex, string> = {
  1: gnomeFullRedUrl,
  2: gnomeFullGreenUrl,
  3: gnomeFullBlueUrl,
  4: gnomeFullYellowUrl,
  5: gnomeFullPinkUrl,
  6: gnomeFullCyanUrl,
  7: gnomeFullOrangeUrl,
};

/**
 * Монета «золото» — финальный PNG-спрайт (owner-directive 2026-06-10). Тот же класс
 * `meta-glyph` + `--gold` модификатор: потребители (currency-hud / reward-block /
 * jewelry-card cost / badge-grid) масштабируют через CSS (`img.meta-glyph` → object-fit),
 * разметка не менялась. Декоративный (alt пустой; aria несёт родитель/строка).
 */
export const ICON_GOLD = `<img class="meta-glyph meta-glyph--gold" src="${coinUrl}" alt="" aria-hidden="true" draggable="false" />`;

/**
 * Осколок «осколки» — финальный PNG-спрайт (owner-directive 2026-06-10). Третий силуэт
 * валюты (НЕ круг монеты, НЕ цветной камень). Тот же swap-path, что монета.
 */
export const ICON_SHARD = `<img class="meta-glyph meta-glyph--shard" src="${shardUrl}" alt="" aria-hidden="true" draggable="false" />`;

/**
 * Самоцвет «камень» — финальный PNG-спрайт (owner-directive 2026-06-10). У каждого
 * цвета СВОЯ форма (gem_size в gems.json); цвет привязан к `colorIndex` (= --ball-N,
 * сверено). Заполненный камень; пустое гнездо той же формы — `gemEmptyGlyph`.
 *
 * @param color colorIndex 1..7 — задаёт камень (= цвет клира, meta-jeweler.md §2.3).
 */
export function gemGlyph(color: ColorIndex): string {
  // Финальный PNG-самоцвет (owner-directive 2026-06-10). Цвет/огранка зашиты в спрайт
  // (форма своя на цвет). Тот же класс `meta-glyph` → потребитель масштабирует через CSS.
  return `<img class="meta-glyph meta-glyph--gem" src="${GEM_IMG[color]}" alt="" aria-hidden="true" draggable="false" />`;
}

/**
 * Пустое гнездо ячейки лотка (art-assets.md §9.2 «пусто»): контур-каст + тусклая
 * цвето-метка цвета X (игрок знает, ЧЕЙ слот). Контраст «закрыто vs пусто» —
 * мгновенный (яркий камень vs тусклое гнездо).
 *
 * @param color colorIndex 1..7 — тусклая цвето-метка пустого слота.
 */
export function gemEmptyGlyph(color: ColorIndex): string {
  // Пустое гнездо = СИЛУЭТ того же спрайта камня (CSS-маска по альфе, см. global.css
  // `.meta-glyph--gem-empty`) → форма гнезда точно по форме камня, тот же размер/позиция,
  // что и заполненный `gemGlyph`. Тусклая цвето-метка цвета X (игрок знает, ЧЕЙ слот) —
  // через `--gem-empty-tint`. owner-directive 2026-06-10 (контуры по камням).
  return `<span class="meta-glyph meta-glyph--gem-empty" aria-hidden="true" style="--gem-mask:url('${GEM_IMG[color]}'); --gem-empty-tint:var(--ball-${color})"></span>`;
}

/**
 * Карат «вечный престиж» (art-assets.md §8.1): бесцветный гранёный бриллиант в
 * лучах — отличим от цветных самоцветов (бесцветность + лучи). В Срезе 1b карата
 * ещё нет в добыче, но HUD/иконка готовы (forward-compat).
 */
export const ICON_KARAT = `<svg class="meta-glyph meta-glyph--karat" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><g stroke="var(--meta-metal-edge)" stroke-width="1" opacity="0.7"><path d="M12 1.5V4M12 20v2.5M1.5 12H4M20 12h2.5M4.6 4.6 6.4 6.4M17.6 17.6 19.4 19.4M19.4 4.6 17.6 6.4M6.4 17.6 4.6 19.4"/></g><polygon points="12,5 17,9 12,18 7,9" fill="var(--meta-karat-fill)" stroke="var(--meta-metal-edge)" stroke-width="1.3" stroke-linejoin="round"/><path d="M7 9h10M12 9v9" stroke="var(--meta-gem-facet)" stroke-width="0.8" opacity="0.6"/></svg>`;

/**
 * Бейдж-самоцвет КАРАТА для карточки артефакта (pivot 2026-06-08 «витрина»):
 * гранёный камень-шестигранник, ВНУТРЬ которого потребитель кладёт ЧИСЛО (уровень
 * карата). Материал/огранка/свечение НЕ заданы тут — их даёт CSS по тиру
 * (`.jewelry-card-karat--tN` ставит `--karat-fill` + блик/свечение), один глиф на все
 * 5 тиров (свопабельный плейсхолдер, как остальные мета-глифы; финальный арт заменит
 * силуэт). Центр камня держим чистым под число; грани — к краям. Light/dark-нейтрально
 * (собственная оправа --meta-metal-edge + блик --meta-gem-facet).
 */
export const KARAT_BADGE_GLYPH = `<svg class="meta-glyph meta-glyph--karat-badge" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><polygon points="12,1.8 20.8,7 20.8,17 12,22.2 3.2,17 3.2,7" fill="var(--karat-fill, var(--meta-stone-dull))" stroke="var(--meta-metal-edge)" stroke-width="1.4" stroke-linejoin="round"/><g stroke="var(--meta-gem-facet)" stroke-width="0.8" opacity="0.5"><path d="M3.2 7 12 4 20.8 7M3.2 17 12 20 20.8 17"/></g><polygon points="12,1.8 20.8,7 20.8,17 12,22.2 3.2,17 3.2,7" fill="var(--karat-sheen, transparent)"/></svg>`;

/**
 * Искра-вспышка артефакта (art-assets.md §5.2 «точка под вспышку»): когда per-tick
 * шанс артефакта сыграл, иконка артефакта вспыхивает. В Срезе 1 артефактов нет →
 * путь инертен (reward-block заложил hook, не показывает искру). Глиф — generic
 * 4-лучевая искра (наследует золотой металл набора).
 */
export const ICON_ARTIFACT_SPARK = `<svg class="meta-glyph meta-glyph--spark" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 2.5 13.8 9.4 20.5 11.5 13.8 13.6 12 20.5 10.2 13.6 3.5 11.5 10.2 9.4Z" fill="var(--meta-gold-fill)" stroke="var(--meta-metal-edge)" stroke-width="1.1" stroke-linejoin="round"/></svg>`;

/**
 * Медальон-глиф ачивки (значок-плейсхолдер) — ОБЩИЙ ПРИМИТИВ (owner-directive 2026-06-21
 * «только через примитивы»). Тот же SVG, что в гриде значков (badge-strip/badge-grid) и
 * тосте — ЕДИНЫЙ источник, чтобы деталь ачивки не плодила 4-ю копию. Класс задаёт
 * потребитель (размер через CSS). Свопабельно на art позже (как coin/shard глифы).
 */
export function achievementMedalGlyph(className = 'meta-glyph meta-glyph--ach-medal'): string {
  return `<svg class="${className}" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="11" r="8" fill="var(--meta-gold-fill)" stroke="var(--meta-metal-edge)" stroke-width="1.4"/><path d="M12 3l1.6 3.4 3.7.5-2.7 2.6.7 3.7L12 11.9 8.7 13.7l.7-3.7L6.7 7.4l3.7-.5Z" fill="var(--meta-gem-facet)" stroke="var(--meta-metal-edge)" stroke-width="0.7" stroke-linejoin="round" opacity="0.85"/><path d="M8 18l1.5 3M16 18l-1.5 3" stroke="var(--meta-metal-edge)" stroke-width="1.2"/></svg>`;
}

// Пять изделий-«артефактов» (ЗВЕРИ) УДАЛЕНЫ (workshop-animals-removal.md): JEWELRY_IMG
// + jewelryGlyph сняты вместе с мастерской. Глифы гномов/камней/валют — ниже, целы.

/**
 * Глиф гнома данного цвета — `<img>` со спрайтом-карточкой (мета «Гномы», §4.2).
 * Декоративный (`aria-hidden`; имя цвета даёт текст карточки). Потребитель
 * (`components/gnome-card`) масштабирует через CSS `.meta-glyph` (как звери/камни) —
 * один источник, swap-path для будущего арт-апдейта.
 *
 * @param color — colorIndex 1..7 (= цвет камня, который добывает гном).
 */
export function gnomeGlyph(color: ColorIndex): string {
  return `<img class="meta-glyph meta-glyph--gnome" src="${GNOME_IMG[color]}" alt="" aria-hidden="true" draggable="false" />`;
}

/**
 * Full-арт гнома в полный рост — `<img>` для экрана «провал» (GN-6, §5.3). БЕЗ камней
 * вокруг (owner-lock §5.3). Потребитель масштабирует через CSS (`object-fit: contain`).
 *
 * @param color — colorIndex 1..7.
 */
export function gnomeFullGlyph(color: ColorIndex): string {
  return `<img class="meta-glyph meta-glyph--gnome-full" src="${GNOME_FULL_IMG[color]}" alt="" aria-hidden="true" draggable="false" />`;
}

/**
 * URL'ы full-арта ВСЕХ 7 гномов (color 1..7 по порядку) — для модулей, которым нужны
 * натуральные размеры всех спрайтов сразу (напр. `lib/gnome-frame-aspect`: единая ширина
 * рамки = max аспект по всем 7 PNG). Один источник списка (этот map) — потребитель не
 * перечисляет URL'ы заново.
 */
export function gnomeFullImageUrls(): readonly string[] {
  return [1, 2, 3, 4, 5, 6, 7].map((c) => GNOME_FULL_IMG[c as ColorIndex]);
}

/**
 * ВСЕ растровые мета-ассеты (7 камней + 7 гномов + full-арт гномов + монета + осколок)
 * — плоский список URL для предзагрузки/декода (`lib/asset-preload`).
 *
 * ЗАЧЕМ: при входе на мета-поверхность DOM пересобирается, и браузер заново ДЕКОДИРУЕТ
 * PNG-спрайты → они «вщёлкиваются» (повторный декод). Предзагрузка + `.decode()` + живые
 * ссылки (Map в asset-preload) держат декодированные изображения, чтобы пересборка DOM
 * показывала их мгновенно. Силуэт-гнездо (`gemEmptyGlyph`) использует ТЕ ЖЕ gem-URL'ы
 * как CSS-маску — прогрев gem-URL покрывает и его. Один источник списка (модульность).
 *
 * «Животные» (5 спрайтов-зверей) удалены отсюда вместе с мастерской
 * (workshop-animals-removal.md).
 */
export const META_ASSET_URLS: readonly string[] = [
  // Камни (7 цветов) — заполняют ячейки И служат маской пустого гнезда.
  gemRedUrl,
  gemGreenUrl,
  gemBlueUrl,
  gemYellowUrl,
  gemPinkUrl,
  gemCyanUrl,
  gemOrangeUrl,
  // Гномы (7 цветов) — спрайты-карточки плашки «Гномы» (warm как звери — карточки
  // рисуются мгновенно без повторного декода при пересборке DOM).
  gnomeRedUrl,
  gnomeGreenUrl,
  gnomeBlueUrl,
  gnomeYellowUrl,
  gnomePinkUrl,
  gnomeCyanUrl,
  gnomeOrangeUrl,
  // Full-арт гномов (7 цветов) — герой карточки-детали (drill-down, owner-directive
  // 2026-06-14 «проваливаться в карточку как у зверей»). Прогрет, чтобы overlay-герой
  // не «вщёлкивался» при первом открытии детали (как звери-герой в beast-detail).
  gnomeFullRedUrl,
  gnomeFullGreenUrl,
  gnomeFullBlueUrl,
  gnomeFullYellowUrl,
  gnomeFullPinkUrl,
  gnomeFullCyanUrl,
  gnomeFullOrangeUrl,
  // Валюты.
  coinUrl,
  shardUrl,
];
